// LearnMore.jsx - Modern Redesigned Component
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LearnMore.css';

const LearnMore = () => {
  return (
    <div className="learn-more-page">
<Helmet>
  <title>CIS Payroll Software | Automate Construction Industry Scheme Payments | PayCIS UK</title>

  <meta
    name="description"
    content="PayCIS automates CIS payroll for UK contractors. Manage subcontractor payments, HMRC compliance, deductions, and returns with Xero, FreeAgent, Revolut & Starling integrations."
  />

  <meta
    name="keywords"
    content="CIS payroll software, Construction Industry Scheme UK, CIS automation, subcontractor payments UK, HMRC CIS returns, PayCIS, contractor payroll system, CIS compliance tool"
  />

  <meta name="author" content="PayCIS" />

  {/* Open Graph */}
  <meta
    property="og:title"
    content="CIS Payroll Software | Automate Construction Payments | PayCIS"
  />
  <meta
    property="og:description"
    content="Automate CIS payroll, deductions, and HMRC returns with PayCIS. Built for UK contractors and subcontractor management."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://paycis.co.uk/learn-more" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta
    name="twitter:title"
    content="CIS Payroll Software | PayCIS UK"
  />
  <meta
    name="twitter:description"
    content="Automate subcontractor payments and CIS compliance with PayCIS."
  />

  <link rel="canonical" href="https://paycis.co.uk/learn-more" />
</Helmet>

      <div className="learn-more-container">
        <h1>CIS Payments<span class="learn-more-gradient-text "> Done Correctly</span></h1>
   
 

        <div className="learn-more-content">
          <section>
  
                      <p className='learn-more-intro-text'>
          Stop correcting subcontractor invoices and second-guessing CIS.
      </p>
                             <p className='learn-more-intro-text'>
              PayCIS runs your subcontractor payment process properly — from payment request to CIS return — without rework, inconsistencies, or manual intervention.
            </p>
                         <p className='learn-more-intro-text'>
              Subcontractors submit their work. You approve it. PayCIS handles deductions and filing.
            </p>

            <h2>Take Control of Subcontractor Payments and CIS</h2>

                             <p className='learn-more-intro-text'>Most contractors already have a system. But it still relies on:</p>
   <div class="feature-pills">
  <div class="pill-item">Reviewing and adjusting incoming invoices</div>
  <div class="pill-item">Interpreting labour and materials</div>
  <div class="pill-item">Checking calculations</div>
  <div class="pill-item">Making sure everything is correct before submission</div>
</div>
            

                           <p className='learn-more-intro-text'>
              That's time-consuming and leaves room for inconsistency. PayCIS replaces that with a structured workflow — so payments, deductions, and returns are handled correctly from the start, not fixed afterwards.
            </p>

            <h2>What Is the Construction Industry Scheme (CIS)?</h2>
                            <p className='learn-more-intro-text'>
              The Construction Industry Scheme requires contractors to deduct and report tax on payments to subcontractors.
            </p>
                         <p className='learn-more-intro-text'>
              Manual processing takes time, increases the risk of mistakes, and can lead to HMRC penalties.
            </p>
                              <p className='learn-more-intro-text'>
              PayCIS automates everything — ensuring you stay compliant while cutting payroll administration time by up to 80%.
            </p>
          </section>

          <section>
            <h2>How PayCIS Works</h2>
            <ol className="numbered-list">
              <li><strong>Register Your Company</strong> – Create your account and link HMRC credentials.</li>
              <li><strong>Add Subcontractors</strong> – Invite subcontractors; PayCIS automatically verifies their tax and VAT status.</li>
              <li><strong>Approve Payments</strong> – Site managers or admins review payment applications online; deductions and payslips are auto-calculated.</li>
              <li><strong>Submit Returns</strong> – CIS returns are automatically compiled and filed with HMRC.</li>
              <li><strong>Analyse and Export</strong> – Instantly export data to your accounting or banking platform and track costs across projects.</li>
            </ol>
          </section>

          <section>
            <h2>Key Features</h2>

            <div className="feature-section">
              <h3>Full CIS Automation</h3>
              <ul>
                <li>Automatic CIS verification with HMRC</li>
                <li>Real-time deduction calculation and monthly returns</li>
                <li>Auto-generated payslips and statements</li>
              </ul>
            </div>

            <div className="feature-section">
              <h3>Advanced Integrations</h3>
              <ul>
                <li>Works with Xero and FreeAgent for instant bookkeeping sync</li>
                <li>Optional Bulk payment facilities via Revolut, Starling Bank, and other UK banks</li>
                <li>Seamless export to accounting and finance systems — no manual re-keying</li>
              </ul>
            </div>

            <div className="feature-section">
              <h3>Smart Compliance & Validation</h3>
              <ul>
                <li>Automatic VAT-number checks</li>
                <li>Bank-account validation before payout</li>
                <li>Auto-contracting between contractor and subcontractor</li>
              </ul>
            </div>

            <div className="feature-section">
              <h3>Multi-User Access</h3>
              <ul>
                <li>Custom roles for site managers, accountants, back-office staff, and payment approvers</li>
                <li>Manage permissions and maintain a clear audit trail</li>
              </ul>
            </div>
          </section>

          <section>
            <h2>Save Time and Money</h2>
            <p className="learn-more-intro-text" style={{ fontSize: '18px', marginBottom: '16px' }}>
              PayCIS drastically reduces the time and cost of managing CIS payroll:
            </p>
            <ul className="benefits-list">
              <li>Your entire payment run in a couple of clicks</li>
              <li>Lower accountant fees through automation and built-in HMRC filing</li>
              <li>No late-return penalties thanks to automatic reminders and submissions</li>
              <li>Fewer disputes and rework through real-time validation of subcontractor data</li>
            </ul>
            <p className="learn-more-intro-text" style={{ fontSize: '18px', marginTop: '24px' }}>
              Average users save hundreds of pounds each month in administrative overhead and reclaim 10–15 hours per week of staff time.
            </p>
          </section>

          <section>
            <h2>Why Choose PayCIS</h2>
            <ul className="checkmark-list">
              <li>✅ HMRC-integrated CIS automation</li>
              <li>✅ Instant verification and error-free deductions</li>
              <li>✅ Real-time accounting and banking integrations</li>
              <li>✅ Multi-user access with secure cloud controls</li>
              <li>✅ Reduced admin cost and faster processing every pay cycle</li>
            </ul>
          </section>

          <section>
            <h2>Who Uses PayCIS</h2>
            <p>Perfect for:</p>
            <ul>
              <li>Construction contractors managing multiple subcontractors</li>
              <li>Developers and building firms handling several sites</li>
              <li>Accountants managing CIS compliance for clients</li>
              <li>Small and medium construction businesses wanting scalability and savings</li>
            </ul>
          </section>

         
        </div>

        
      </div>
 <section className="cta-section">
            <h3>Get Started Today</h3>
            <p>
              Switch to PayCIS and start saving time and money from your next payroll cycle.
            </p>
            <p>
              Enjoy a free extended trial — no setup costs, no contracts.
            </p>
            <div className="cta-buttons">
              <Link to="/register" className="cta-button primary">
                Get Started
              </Link>
              <Link to="/contact" className="cta-button secondary">
                Book a Demo
              </Link>
            </div>
          </section>
    </div>
  );
};

export default LearnMore;